import { makeAutoObservable, runInAction } from 'mobx'
import type { Notify } from '@/Ship/Contracts/Notify'
import { CreateStandaloneAssignmentBatchTask } from '../Tasks/CreateStandaloneAssignmentBatchTask'

/** A single editable driver/vehicle pair in the multi-create drawer. */
export interface DriverRow {
    id: number
    driver: string | null
    vehicle: string | null
}

/**
 * Стан drawer-а множинного створення standalone-задач.
 *
 * Тримає спільні поля (кластер/регіон, об'єкти, відповідальний, пункти, маршрут,
 * дата, маса, ходки, isHired/carrier, dispatcher) + список пар «водій + ТЗ». На submit
 * формує один batch-запит: { shared, assignments: [{driver, vehicle}, ...] }.
 */
export class MultiAssignmentDrawerStore {
    open = false
    submitting = false

    // Спільні значення (IRI/скаляри), як їх очікує бекенд.
    shared: Record<string, any> = {}

    rows: DriverRow[] = [{ id: 1, driver: null, vehicle: null }]
    private nextRowId = 2

    constructor() {
        makeAutoObservable(this)
    }

    openDrawer(): void {
        this.reset()
        this.open = true
    }

    closeDrawer(): void {
        this.open = false
    }

    reset(): void {
        this.shared = {}
        this.rows = [{ id: 1, driver: null, vehicle: null }]
        this.nextRowId = 2
    }

    setShared(field: string, value: any): void {
        this.shared = { ...this.shared, [field]: value }
        // Зміна isHired/carrier робить обрані водіїв/ТЗ невалідними — скидаємо їх,
        // щоб не відправити пару з іншого перевізника (бекенд-фільтр не гарантує).
        if (field === 'isHired' || field === 'carrier') {
            this.rows = this.rows.map((r) => ({ ...r, driver: null, vehicle: null }))
        }
        // Регіон обирається серед регіонів кластера — зміна кластера його скидає
        if (field === 'cluster' && this.shared['region']) {
            this.shared = { ...this.shared, region: null }
        }
    }

    get isHired(): boolean {
        return !!this.shared['isHired']
    }

    /**
     * Чи ввів користувач щось (спільне поле або водія/ТЗ у рядку) — для підтвердження
     * закриття. Порожні значення й вимкнений перемикач змінами не вважаються; доданий
     * порожній рядок — теж.
     */
    get isDirty(): boolean {
        const filled = (value: unknown) => value !== null && value !== undefined && value !== '' && value !== false
        return Object.values(this.shared).some(filled) || this.rows.some((row) => !!row.driver || !!row.vehicle)
    }

    get carrierIri(): string | null {
        const c = this.shared['carrier']
        return typeof c === 'string' ? c : (c?.['@id'] ?? null)
    }

    setRowField(id: number, field: 'driver' | 'vehicle', value: string | null): void {
        this.rows = this.rows.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    }

    addRow(): void {
        this.rows = [...this.rows, { id: this.nextRowId++, driver: null, vehicle: null }]
    }

    removeRow(id: number): void {
        if (this.rows.length <= 1) return
        this.rows = this.rows.filter((r) => r.id !== id)
    }

    /** Rows that carry at least a driver (the only required per-row field). */
    private validRows(): DriverRow[] {
        return this.rows.filter((r) => !!r.driver)
    }

    async submit(notify: Notify, onSuccess?: () => void): Promise<void> {
        const rows = this.validRows()
        if (rows.length === 0) {
            notify.error('Додайте щонайменше одного водія')
            return
        }
        if (!this.shared['dispatcher']) {
            notify.error('Оберіть відповідального диспетчера')
            return
        }
        if (!this.shared['cluster']) {
            notify.error('Оберіть кластер')
            return
        }
        if (!this.shared['region']) {
            notify.error('Оберіть регіон')
            return
        }

        this.submitting = true
        try {
            await CreateStandaloneAssignmentBatchTask({
                shared: this.shared,
                assignments: rows.map((r) => ({ driver: r.driver, vehicle: r.vehicle })),
            })
            notify.success(`Створено завдань: ${rows.length}`)
            runInAction(() => {
                this.open = false
                this.reset()
            })
            onSuccess?.()
        } catch (e: any) {
            const msg = e?.response?.message || e?.message || 'Не вдалося створити завдання'
            notify.error(msg)
        } finally {
            runInAction(() => {
                this.submitting = false
            })
        }
    }
}
